/**
 * TrialStartCard — the trial offer where unpaid creators actually are
 * (TRIAL_PLAN.md §8.2b). `has_dashboard_access()` routes every unpaid account
 * to the Fan Portal, so this card lives there and in Account → Billing.
 * Eligibility (one per account, lapsed subscribers included, D34) is the
 * backend's `trial.eligible`.
 *
 * Oct 2026 (Randy, fan-first pass) — two corrections:
 *   • The blue is gone. The trial now wears the same sunset-green and amber
 *     the landing page wears, so it reads as part of My Silo rather than a
 *     SaaS banner bolted on.
 *   • `mini` is the Fan Portal's variant: one small button under the Creator
 *     and Creator Pro cards. The trial is a third option, not the headline —
 *     tapping it opens the details, which is where the terms belong.
 */
import { useState } from "react";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";
import { useTrial, money } from "@/lib/trial";
import TrialStartModal from "@/components/TrialStartModal";

export default function TrialStartCard({ variant = "portal" }) {
  const { trial } = useTrial();
  const [open, setOpen] = useState(false);
  if (!trial?.eligible || trial?.live) return null;

  const days = trial.trial_days || 14;
  const fee = money(trial.subscription_cents);

  if (variant === "mini") {
    return (
      <>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="fan-portal-trial-mini">
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="fan-portal-trial-start-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[#4A5D4E]/30 bg-[#E8EBE5]/70 text-[#3A4A3E] text-xs font-medium hover:bg-[#DCE3D8] hover:border-[#4A5D4E]/50 transition-colors"
          >
            <Sparkle size={12} weight="duotone" className="text-[#C9922F]" />
            Try {days} days free
            <ArrowRight size={11} weight="bold" />
          </button>
        </div>
        <TrialStartModal open={open} onOpenChange={setOpen} />
      </>
    );
  }

  return (
    <>
      <div
        className={
          variant === "billing"
            ? "siloart-encased rounded-lg border border-[#4A5D4E]/25 bg-[#E8EBE5] px-5 py-4"
            : "siloart-encased relative overflow-hidden rounded-lg bg-gradient-to-br from-[#1C1B1A] to-[#2C3B2F] text-[#FDFCF8] px-5 py-5 mt-6"
        }
        data-testid={variant === "billing" ? "billing-trial-start" : "fan-portal-trial-card"}
      >
        {variant !== "billing" && (
          <div className="absolute -right-4 -top-4 opacity-10"><Sparkle size={96} weight="duotone" /></div>
        )}
        <div className="relative z-10">
          <div className={`text-[9px] uppercase tracking-[0.28em] mb-1.5 flex items-center gap-1.5 ${
            variant === "billing" ? "text-[#4A5D4E]" : "text-[#FFC24A]"
          }`}>
            <Sparkle size={10} weight="duotone" /> Free for {days} days
          </div>
          <div className={`font-serif-display text-lg sm:text-xl leading-tight ${
            variant === "billing" ? "text-[#1C1B1A]" : "text-white"
          }`}>
            You have {days} days of the full toolkit waiting.
          </div>
          <p className={`text-[13px] mt-1.5 max-w-xl ${
            variant === "billing" ? "text-[#4A5D4E]" : "text-[#E5E5E0]/90"
          }`}>
            Build your silo, train your twin, answer your comments — free for {days} days.
            {" "}{fee}/mo starts on day {days + 1}, and only the tools you choose to keep are added.
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid={variant === "billing" ? "billing-trial-start-btn" : "fan-portal-trial-start-btn"}
            className={`mt-3.5 px-5 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              variant === "billing"
                ? "bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]"
                : "bg-[#F5EFE2] text-[#14110C] hover:bg-[#FFC24A]"
            }`}
          >
            Start my {days} days <ArrowRight size={15} />
          </button>
        </div>
      </div>
      <TrialStartModal open={open} onOpenChange={setOpen} />
    </>
  );
}
